import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { Mail, Send, CheckCircle, XCircle, Eye, MousePointer, AlertTriangle, RefreshCw, ArrowUp, ArrowDown, Clock, BarChart3, TrendingUp, Search } from 'lucide-react';
import { getToken } from '../services/tokenService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EVENT_LABELS = {
  delivered: { label: 'Delivre', color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle },
  opened: { label: 'Ouvert', color: 'bg-blue-100 text-blue-800', icon: Eye },
  clicked: { label: 'Clique', color: 'bg-violet-100 text-violet-800', icon: MousePointer },
  soft_bounce: { label: 'Soft Bounce', color: 'bg-amber-100 text-amber-800', icon: AlertTriangle },
  hard_bounce: { label: 'Hard Bounce', color: 'bg-red-100 text-red-800', icon: XCircle },
  requests: { label: 'Envoye', color: 'bg-sky-100 text-sky-800', icon: Send },
  blocked: { label: 'Bloque', color: 'bg-gray-100 text-gray-800', icon: XCircle },
  invalid: { label: 'Invalide', color: 'bg-red-100 text-red-700', icon: XCircle },
  deferred: { label: 'Differe', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  complaint: { label: 'Plainte', color: 'bg-red-200 text-red-900', icon: AlertTriangle },
  error: { label: 'Erreur', color: 'bg-red-100 text-red-700', icon: XCircle },
  unsubscribed: { label: 'Desabonne', color: 'bg-gray-200 text-gray-700', icon: XCircle },
};

function StatCard({ title, value, icon: Icon, trend, color, subtitle }) {
  return (
    <Card data-testid={`stat-card-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{title}</p>
            <p className="text-2xl font-bold mt-1" style={{ color: color || '#1e293b' }}>{value}</p>
            {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
          </div>
          <div className="p-2.5 rounded-xl" style={{ backgroundColor: (color || '#3b82f6') + '15' }}>
            <Icon size={20} style={{ color: color || '#3b82f6' }} />
          </div>
        </div>
        {trend !== undefined && (
          <div className="flex items-center gap-1 mt-2 text-xs">
            {trend >= 0 ? (
              <ArrowUp size={12} className="text-emerald-500" />
            ) : (
              <ArrowDown size={12} className="text-red-500" />
            )}
            <span className={trend >= 0 ? 'text-emerald-600' : 'text-red-600'}>
              {Math.abs(trend)}%
            </span>
            <span className="text-gray-400">vs periode prec.</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function MiniBarChart({ data, maxValue, color }) {
  if (!data || data.length === 0) return null;
  const max = maxValue || Math.max(...data.map(d => d.value), 1);
  return (
    <div className="flex items-end gap-[2px] h-12">
      {data.map((d, i) => (
        <div
          key={i}
          className="flex-1 rounded-t-sm transition-all hover:opacity-80"
          style={{
            height: `${Math.max((d.value / max) * 100, 2)}%`,
            backgroundColor: color || '#3b82f6',
            minWidth: '3px',
          }}
          title={`${d.label}: ${d.value}`}
        />
      ))}
    </div>
  );
}

export default function EmailTrackingDashboard() {
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [period, setPeriod] = useState(30);
  const [eventFilter, setEventFilter] = useState('');
  const [searchEmail, setSearchEmail] = useState('');
  const [activeView, setActiveView] = useState('overview');

  const getAuthHeader = () => ({
    Authorization: `Bearer ${getToken()}`,
  });

  const loadStats = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BACKEND_URL}/api/email-tracking/stats?days=${period}`, {
        headers: getAuthHeader(),
      });
      setStats(res.data);
    } catch {
      toast.error('Erreur chargement des statistiques email');
    } finally {
      setLoading(false);
    }
  }, [period]);

  const loadEvents = useCallback(async () => {
    try {
      setEventsLoading(true);
      const params = new URLSearchParams({ limit: '50', days: '7' });
      if (eventFilter) params.set('event_type', eventFilter);
      if (searchEmail) params.set('email', searchEmail);

      const res = await axios.get(`${BACKEND_URL}/api/email-tracking/events?${params}`, {
        headers: getAuthHeader(),
      });
      setEvents(res.data.events || []);
    } catch {
    } finally {
      setEventsLoading(false);
    }
  }, [eventFilter, searchEmail]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    if (activeView === 'events') loadEvents();
  }, [activeView, loadEvents]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch { return dateStr; }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('fr-FR', {
        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
      });
    } catch { return dateStr; }
  };

  const dailyChartData = stats?.daily?.map(d => ({
    label: formatDate(d.date),
    value: d.delivered || 0,
  })) || [];

  const openChartData = stats?.daily?.map(d => ({
    label: formatDate(d.date),
    value: d.unique_opens || 0,
  })) || [];

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="email-tracking-loading">
        <RefreshCw className="animate-spin text-blue-500 mr-2" size={20} />
        <span className="text-gray-500">Chargement des statistiques...</span>
      </div>
    );
  }

  const t = stats?.totals || {};
  const r = stats?.rates || {};

  return (
    <div className="space-y-5" data-testid="email-tracking-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Suivi des Emails</h2>
          <p className="text-sm text-gray-500">
            Statistiques Brevo &mdash; {stats?.period?.start} au {stats?.period?.end}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {[7, 14, 30, 60].map(d => (
            <Button
              key={d}
              variant={period === d ? 'default' : 'outline'}
              size="sm"
              onClick={() => setPeriod(d)}
              data-testid={`period-btn-${d}`}
            >
              {d}j
            </Button>
          ))}
          <Button variant="outline" size="sm" onClick={loadStats} data-testid="refresh-stats-btn">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="email-kpi-cards">
        <StatCard title="Envoyes" value={t.requests || 0} icon={Send} color="#3b82f6" subtitle={`${period} derniers jours`} />
        <StatCard title="Delivres" value={t.delivered || 0} icon={CheckCircle} color="#10b981" subtitle={`Taux: ${r.delivery_rate || 0}%`} />
        <StatCard title="Ouverts" value={t.unique_opens || 0} icon={Eye} color="#8b5cf6" subtitle={`Taux: ${r.open_rate || 0}%`} />
        <StatCard title="Cliques" value={t.unique_clicks || 0} icon={MousePointer} color="#f59e0b" subtitle={`Taux: ${r.click_rate || 0}%`} />
      </div>

      {/* Rate bars + mini charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <BarChart3 size={16} /> Emails delivres / jour
            </CardTitle>
          </CardHeader>
          <CardContent>
            <MiniBarChart data={dailyChartData} color="#10b981" />
            <div className="flex justify-between mt-2 text-[10px] text-gray-400">
              <span>{dailyChartData[0]?.label}</span>
              <span>{dailyChartData[dailyChartData.length - 1]?.label}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <TrendingUp size={16} /> Ouvertures uniques / jour
            </CardTitle>
          </CardHeader>
          <CardContent>
            <MiniBarChart data={openChartData} color="#8b5cf6" />
            <div className="flex justify-between mt-2 text-[10px] text-gray-400">
              <span>{openChartData[0]?.label}</span>
              <span>{openChartData[openChartData.length - 1]?.label}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Rates detail */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-gray-600">Taux de performance</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[
              { label: 'Delivrabilite', value: r.delivery_rate, color: '#10b981' },
              { label: 'Ouverture', value: r.open_rate, color: '#8b5cf6' },
              { label: 'Clic', value: r.click_rate, color: '#f59e0b' },
              { label: 'Rebond', value: r.bounce_rate, color: '#ef4444' },
            ].map(({ label, value, color }) => (
              <div key={label} className="flex items-center gap-3">
                <span className="text-xs text-gray-500 w-24">{label}</span>
                <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-700"
                    style={{ width: `${Math.min(value || 0, 100)}%`, backgroundColor: color }}
                  />
                </div>
                <span className="text-xs font-semibold w-12 text-right">{value || 0}%</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Bounce/Error details */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard title="Hard Bounces" value={t.hard_bounces || 0} icon={XCircle} color="#ef4444" />
        <StatCard title="Soft Bounces" value={t.soft_bounces || 0} icon={AlertTriangle} color="#f59e0b" />
        <StatCard title="Bloques" value={t.blocked || 0} icon={XCircle} color="#6b7280" />
        <StatCard title="Invalides" value={t.invalid || 0} icon={AlertTriangle} color="#dc2626" />
      </div>

      {/* Tabs: Overview / Events */}
      <div className="flex gap-2 border-b pb-1">
        <Button
          variant={activeView === 'overview' ? 'default' : 'ghost'}
          size="sm"
          onClick={() => setActiveView('overview')}
          data-testid="tab-overview"
        >
          <BarChart3 size={14} className="mr-1" /> Vue d'ensemble
        </Button>
        <Button
          variant={activeView === 'events' ? 'default' : 'ghost'}
          size="sm"
          onClick={() => setActiveView('events')}
          data-testid="tab-events"
        >
          <Mail size={14} className="mr-1" /> Evenements recents
        </Button>
      </div>

      {/* Events Table */}
      {activeView === 'events' && (
        <Card data-testid="events-section">
          <CardHeader className="pb-2">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
              <CardTitle className="text-sm font-medium">Evenements des 7 derniers jours</CardTitle>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search size={14} className="absolute left-2 top-2.5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Filtrer par email..."
                    value={searchEmail}
                    onChange={e => setSearchEmail(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && loadEvents()}
                    className="pl-7 pr-3 py-1.5 text-xs border rounded-md w-48"
                    data-testid="search-email-input"
                  />
                </div>
                <select
                  value={eventFilter}
                  onChange={e => setEventFilter(e.target.value)}
                  className="text-xs border rounded-md px-2 py-1.5"
                  data-testid="event-filter-select"
                >
                  <option value="">Tous</option>
                  <option value="delivered">Delivres</option>
                  <option value="opened">Ouverts</option>
                  <option value="clicked">Cliques</option>
                  <option value="hard_bounce">Hard Bounce</option>
                  <option value="soft_bounce">Soft Bounce</option>
                </select>
                <Button variant="outline" size="sm" onClick={loadEvents} data-testid="refresh-events-btn">
                  <RefreshCw size={12} className={eventsLoading ? 'animate-spin' : ''} />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {eventsLoading ? (
              <div className="flex justify-center py-8">
                <RefreshCw className="animate-spin text-gray-400" size={18} />
              </div>
            ) : events.length === 0 ? (
              <p className="text-center text-gray-400 py-8 text-sm">Aucun evenement trouve</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="events-table">
                  <thead>
                    <tr className="border-b text-gray-500">
                      <th className="text-left py-2 px-2 font-medium">Date</th>
                      <th className="text-left py-2 px-2 font-medium">Destinataire</th>
                      <th className="text-left py-2 px-2 font-medium">Sujet</th>
                      <th className="text-left py-2 px-2 font-medium">Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((evt, i) => {
                      const meta = EVENT_LABELS[evt.event] || EVENT_LABELS.requests;
                      const EventIcon = meta.icon;
                      return (
                        <tr key={i} className="border-b last:border-0 hover:bg-gray-50 transition-colors">
                          <td className="py-2 px-2 text-gray-500 whitespace-nowrap">{formatDateTime(evt.date)}</td>
                          <td className="py-2 px-2 font-medium text-gray-700 max-w-[200px] truncate">{evt.email}</td>
                          <td className="py-2 px-2 text-gray-600 max-w-[250px] truncate">{evt.subject || '—'}</td>
                          <td className="py-2 px-2">
                            <Badge className={`${meta.color} text-[10px] gap-1`}>
                              <EventIcon size={10} />
                              {meta.label}
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
